import {
    Component,
    ElementRef,
    Inject,
    PLATFORM_ID,
    ViewChild,
    NgZone,
    ChangeDetectorRef,
    AfterViewInit,
    DoCheck,
    OnDestroy,
} from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { FormlyAttributeEvent } from "@ngx-formly/core/lib/models";

@Component({
    standalone: false,
    templateUrl: "./control.component.html",
})
export class FormlyTextArea extends FieldType<FieldTypeConfig> implements AfterViewInit, DoCheck, OnDestroy {
    @ViewChild("ta") control: ElementRef;
    private maxHeight: number = 500;
    private keyupEvent: FormlyAttributeEvent = null;
    private isAdjusting: boolean = false;
    private lastValue: any;
    private lastWidth: number = 0;
    private resizeObserver: ResizeObserver;
    private resizeFrame: number;
    needsScroll: boolean = false;

    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        private ngZone: NgZone,
        private cdr: ChangeDetectorRef
    ) {
        super();
    }

    ngOnInit() {
        if (this.props.maxHeight) this.maxHeight = this.props.maxHeight;

        if (this.props.keyup) {
            this.keyupEvent = this.props.keyup;
            this.props.keyup = () => {
                if (this.keyupEvent) this.keyupEvent(this, "keyup");
            };
        }
    }

    ngAfterViewInit() {
        if (!this.control?.nativeElement || !isPlatformBrowser(this.platformId)) return;

        // Override any scroll behavior that might interfere
        this.control.nativeElement.style.scrollBehavior = 'auto';
        this.lastValue = this.formControl?.value;
        this.scheduleResize();

        // Re-measure when the textarea becomes visible (tabs/collapsed panels) or its width changes
        if (typeof ResizeObserver !== "undefined") {
            this.ngZone.runOutsideAngular(() => {
                this.resizeObserver = new ResizeObserver(() => {
                    const width = this.control.nativeElement.offsetWidth;
                    if (width !== this.lastWidth) {
                        this.lastWidth = width;
                        this.scheduleResize();
                    }
                });
                this.resizeObserver.observe(this.control.nativeElement);
            });
        }
    }

    ngDoCheck() {
        // Record data usually arrives after the view is built (and view mode is disabled, so no
        // click/focus), so resize whenever the value changes rather than only on user interaction
        const value = this.formControl?.value;
        if (this.control?.nativeElement && value !== this.lastValue) {
            this.lastValue = value;
            this.scheduleResize();
        }
    }

    ngOnDestroy() {
        this.resizeObserver?.disconnect();
        if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
    }

    keyup(event: Event) {
        if (this.props.keyup) return this.props.keyup(this, "keyup");
    }

    onInput(event: Event) {
        this.adjustHeight();
        this.checkScrollNeeded();
    }

    textAreaChanged($event) {
        if (this.props.change) this.props.change(this);
    }

    private scheduleResize() {
        if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => {
            this.resizeFrame = null;
            this.ngZone.run(() => {
                this.adjustHeight();
                this.checkScrollNeeded();
            });
        });
    }

    private adjustHeight() {
        if (this.control?.nativeElement && !this.isAdjusting) {
            const textarea = this.control.nativeElement;
            // Hidden (e.g. inactive tab) - nothing to measure yet, ResizeObserver will retry once shown
            if (!textarea.offsetWidth) return;

            this.isAdjusting = true;

            const currentHeight = parseInt(textarea.style.height) || textarea.offsetHeight;

            // Measure on a hidden clone so the real textarea never collapses (avoids page jumping while typing).
            // The clone must have the same width as the real textarea or line wrapping, and so height, differs.
            const clone = textarea.cloneNode(true) as HTMLTextAreaElement;
            clone.value = textarea.value;
            clone.style.position = 'absolute';
            clone.style.visibility = 'hidden';
            clone.style.left = '-9999px';
            clone.style.top = '0';
            clone.style.width = textarea.offsetWidth + 'px';
            clone.style.height = 'auto';
            clone.style.minHeight = '32px';
            clone.style.maxHeight = 'none';

            document.body.appendChild(clone);
            // scrollHeight excludes borders, but form-control is border-box
            const borders = clone.offsetHeight - clone.clientHeight;
            const requiredHeight = Math.min(Math.max(clone.scrollHeight + borders, 32), this.maxHeight);
            document.body.removeChild(clone);

            // Only adjust if height actually needs to change
            if (Math.abs(requiredHeight - currentHeight) > 2) {
                textarea.style.height = requiredHeight + 'px';
            }

            this.isAdjusting = false;
        }
    }

    private checkScrollNeeded() {
        if (this.control?.nativeElement) {
            const textarea = this.control.nativeElement;
            const currentHeight = parseInt(textarea.style.height) || textarea.offsetHeight;

            // Check if content height exceeds max height
            this.needsScroll = textarea.scrollHeight > this.maxHeight ||
                currentHeight >= this.maxHeight;

            // Trigger change detection
            this.cdr.detectChanges();
        }
    }
}
